import { Feature } from 'ol';
import { FeatureLike } from 'ol/Feature';
import { Point, Polygon } from 'ol/geom';
import {
  Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text as TextStyle,
} from 'ol/style';
import { designTokens } from '../../design-system/tokens';
import { VenueSeatType } from '../../models/venue-seat';
import { VenueShapeDTO } from '../../models/venue';

/** Visual state of a place on any of the three maps (buyer, editor, event map). */
export type PlaceState =
  | 'available'
  | 'selected'
  | 'sold'
  | 'held'
  | 'blocked'
  | 'unassigned'
  | 'locked'
  | 'invalid'
  | 'preview';

export interface PlaceGeometry {
  type: VenueSeatType;
  width: number | null;
  height: number | null;
  diameter: number | null;
  rotation: number;
  seat_count: number;
  seat_number: number;
}

export type ThemeMode = 'light' | 'dark';

/** Fingers are 44 px wide: nothing tappable is smaller than that. */
export const MIN_HIT_PX = 44;

/** Levels of detail, in pixels per meter. */
export const LOD = {
  chairs: 7,
  tableNumbers: 14,
  seatNumbers: 24,
} as const;

export const PLACE_DEFAULT_SIZE: Record<VenueSeatType, { width: number; height: number; diameter: number | null }> = {
  seat: { width: 0.5, height: 0.5, diameter: null },
  round_table: { width: 1.2, height: 1.2, diameter: 1.2 },
  square_table: { width: 0.9, height: 0.9, diameter: null },
  long_table: { width: 2.4, height: 0.8, diameter: null },
};

export const PLACE_TYPE_LABELS: Record<VenueSeatType, { singular: string; plural: string; prefix: string }> = {
  seat: { singular: 'Butaca', plural: 'butacas', prefix: 'Butaca' },
  round_table: { singular: 'Mesa redonda', plural: 'mesas', prefix: 'Mesa' },
  square_table: { singular: 'Mesa cuadrada', plural: 'mesas', prefix: 'Mesa' },
  long_table: { singular: 'Tablón', plural: 'tablones', prefix: 'Tablón' },
};

export const placeSize = (place: Pick<PlaceGeometry, 'type' | 'width' | 'height' | 'diameter'>) => {
  const defaults = PLACE_DEFAULT_SIZE[place.type] ?? PLACE_DEFAULT_SIZE.seat;
  const width = place.width ?? defaults.width;
  const height = place.height ?? defaults.height;
  const diameter = place.diameter ?? defaults.diameter ?? width;
  return { width, height, diameter };
};

const CHAIR_GAP_M = 0.32;

/**
 * Chair centres around a table, in meters relative to the table centre and
 * before rotation. Seats have none: the seat is the chair.
 */
export const chairOffsets = (place: PlaceGeometry): [number, number][] => {
  const { width, height, diameter } = placeSize(place);
  const count = Math.max(0, place.seat_count);
  if (place.type === 'seat' || count === 0) {
    return [];
  }
  if (place.type === 'round_table') {
    const r = diameter / 2 + CHAIR_GAP_M;
    return Array.from({ length: count }, (_, i) => {
      const a = (2 * Math.PI * i) / count - Math.PI / 2;
      return [r * Math.cos(a), r * Math.sin(a)];
    });
  }
  // Rectangular tables: chairs on the long sides, extra ones on the ends.
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  const perSide = Math.ceil(count / 2);
  const ends = Math.max(0, count - perSide * 2);
  const offsets: [number, number][] = [];
  for (let i = 0; i < perSide; i += 1) {
    const along = -long / 2 + (long * (i + 0.5)) / perSide;
    offsets.push([along, short / 2 + CHAIR_GAP_M]);
    if (offsets.length < count - ends) {
      offsets.push([along, -short / 2 - CHAIR_GAP_M]);
    }
  }
  if (ends > 0) {
    offsets.push([long / 2 + CHAIR_GAP_M, 0]);
  }
  return offsets.slice(0, count);
};

export const rotatePoint = ([x, y]: [number, number], degrees: number): [number, number] => {
  // Clockwise degrees on a y-up map.
  const a = (-degrees * Math.PI) / 180;
  return [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
};

export const hexToRgba = (hex: string, alpha: number): string => {
  const fallback = `rgba(128, 128, 128, ${alpha})`;
  if (!hex) {
    return fallback;
  }
  let normalized = hex.trim().replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map((c) => c + c).join('');
  }
  if (normalized.length !== 6) {
    return fallback;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return fallback;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Hex colours get the alpha; rgba strings are already final.
export const withAlpha = (color: string, alpha: number) => (color.startsWith('#') ? hexToRgba(color, alpha) : color);

const { neutral } = designTokens.colors;
const brand = designTokens.colors.brand.primary[500];
const errorColor = designTokens.colors.semantic.error[500];

export const mapPalette = (theme: ThemeMode) => ({
  surface: theme === 'dark' ? neutral[900] : neutral[0],
  ink: theme === 'dark' ? neutral[50] : neutral[900],
  muted: theme === 'dark' ? neutral[500] : neutral[400],
  sold: theme === 'dark' ? neutral[700] : neutral[300],
  soldInk: theme === 'dark' ? neutral[500] : neutral[500],
  stage: theme === 'dark' ? neutral[950] : neutral[900],
  walkway: theme === 'dark' ? neutral[800] : neutral[600],
  fixture: theme === 'dark' ? neutral[700] : neutral[400],
  halo: theme === 'dark' ? 'rgba(9,9,11,0.85)' : 'rgba(255,255,255,0.85)',
  selected: brand,
  invalid: errorColor,
});

interface PlaceStyleOptions {
  color: string;
  state: PlaceState;
  resolution: number;
  pxPerM: number;
  theme: ThemeMode;
  // Fade everything that is not part of the sector the buyer is looking at.
  dimmed?: boolean;
  label?: string;
  // Extra ring to show a selection in the editors without changing the fill.
  outlined?: boolean;
}

// Rendering 1000 places used to create several canvas images per place on
// every zoom frame (OL draws each CircleStyle/RegularShape to its own canvas).
// Now images are shared between places with the same look, and the zoom is
// rounded to steps of ~12 % so a zoom animation reuses them.
const ZOOM_STEP = 1.12;
const quantizePxPerM = (pxPerM: number) => ZOOM_STEP ** Math.round(Math.log(Math.max(pxPerM, 0.01)) / Math.log(ZOOM_STEP));

const MAX_CACHE = 5000;
const imageCache = new Map<string, CircleStyle | RegularShape>();
const styleCache = new Map<string, Style>();

const cached = <T>(cache: Map<string, T>, key: string, build: () => T): T => {
  const hit = cache.get(key);
  if (hit) {
    return hit;
  }
  if (cache.size > MAX_CACHE) {
    cache.clear();
  }
  const value = build();
  cache.set(key, value);
  return value;
};

/**
 * Number drawn on a place. Old venues store composite numbers (27026 = row 27,
 * seat 26), so the name wins: the number after Butaca/Asiento/Mesa/Tablón, or
 * the last number of the name ("Fila 27 - Butaca 26" → 26). Falls back to
 * seat_number when it is short.
 */
export const placeNumberLabel = (name: string | null | undefined, seatNumber: number) => {
  if (name) {
    const named = name.match(/(?:butaca|asiento|silla|mesa|tabl[oó]n)\s*(?:n[°º.]?\s*)?(\d+)/i);
    if (named) {
      return named[1];
    }
    const numbers = name.match(/\d+/g);
    if (numbers?.length) {
      return numbers[numbers.length - 1];
    }
  }
  return seatNumber > 0 && seatNumber < 10000 ? String(seatNumber) : '';
};

// The butaca silhouette of the old map: flat top, rounded bottom. Drawn as a
// circle plus a half-height square over its top half, both vector shapes so
// they scale with the zoom (an SVG icon kept its raster size).
const seatSilhouette = (fillColor: string, strokeColor: string, lineWidth: number, sizePx: number, rotation: number): Style[] => {
  const size = Math.round(sizePx * 2) / 2;
  const r = size / 2;
  const angle = (rotation * Math.PI) / 180;
  // Centre of the top half, rotated with the seat (pixels, y up).
  const displacement = [(r / 2) * Math.sin(angle), (r / 2) * Math.cos(angle)];
  const parts = (key: string, zIndex: number, fill: Fill, stroke?: Stroke) => [
    cached(styleCache, `seat-round|${key}`, () => new Style({
      image: new CircleStyle({ radius: r, fill, stroke }),
      zIndex,
    })),
    cached(styleCache, `seat-top|${key}`, () => new Style({
      image: new RegularShape({
        points: 4,
        radius: Math.SQRT2 * r,
        angle: Math.PI / 4,
        scale: [1, 0.5],
        rotation: angle,
        rotateWithView: true,
        displacement,
        fill,
        stroke,
      }),
      zIndex,
    })),
  ];
  const fill = new Fill({ color: fillColor });
  const body = parts(`${fillColor}|${size}|${rotation}`, 2, fill);
  if (strokeColor === fillColor) {
    return body;
  }
  // Outline underneath: a stroke twice as wide, half of it covered by the body,
  // so the circle and the backrest read as one silhouette without a seam.
  const outline = parts(`outline|${strokeColor}|${lineWidth}|${size}|${rotation}`, 1, new Fill({ color: strokeColor }), new Stroke({ color: strokeColor, width: lineWidth * 2 }));
  return [...outline, ...body];
};

/**
 * Style of one place. Sizes are real: the shape is width × pxPerM pixels, so
 * a table and a seat keep their proportion at any zoom. Tapping is handled by
 * placeHitTest, not by the drawing.
 */
export const placeStyle = (place: PlaceGeometry, coordinate: number[], options: PlaceStyleOptions): Style[] => {
  const {
    color, state, theme, dimmed, label, outlined,
  } = options;
  const pxPerM = quantizePxPerM(options.pxPerM);
  const palette = mapPalette(theme);
  const { width, height, diameter } = placeSize(place);
  const alpha = dimmed ? 0.25 : 1;

  let fill = palette.surface;
  let stroke = color;
  let { ink } = palette;
  switch (state) {
    case 'selected':
      fill = palette.selected;
      stroke = palette.selected;
      ({ 0: ink } = neutral);
      break;
    case 'sold':
    case 'held':
    case 'locked':
      fill = palette.sold;
      stroke = palette.sold;
      ink = palette.soldInk;
      break;
    case 'blocked':
    case 'unassigned':
      fill = palette.surface;
      stroke = palette.muted;
      ink = palette.muted;
      break;
    case 'invalid':
      fill = hexToRgba(errorColor, 0.2);
      stroke = errorColor;
      break;
    case 'preview':
      fill = hexToRgba(brand, 0.35);
      stroke = brand;
      break;
    default:
      // Available: filled with the sector colour, like the old seat icons.
      fill = place.type === 'seat' ? color : hexToRgba(color, 0.85);
      ({ 0: ink } = neutral);
  }

  const strokeWidth = Math.max(1, Math.min(3, pxPerM * 0.06));
  const fillColor = withAlpha(fill, alpha);
  const strokeColor = outlined ? palette.selected : withAlpha(stroke, alpha);
  const lineWidth = outlined ? strokeWidth + 2 : strokeWidth;
  const styles: Style[] = [];

  // No invisible tap disc: hits are resolved with the spatial index
  // (placeHitTest.ts), which already gives every place MIN_HIT_PX of reach.
  const visualRadiusPx = (Math.max(width, height, diameter) / 2) * pxPerM;

  if (place.type !== 'seat' && pxPerM >= LOD.chairs) {
    const chairRadius = Math.round(Math.max(2, 0.2 * pxPerM) * 2) / 2;
    let chairColor = palette.sold;
    if (state === 'available' || state === 'unassigned' || state === 'preview') {
      chairColor = state === 'available' ? color : palette.muted;
    } else if (state === 'selected') {
      chairColor = palette.selected;
    }
    const chairFillColor = withAlpha(chairColor, alpha * 0.6);
    const chairImage = cached(imageCache, `chair|${chairRadius}|${chairFillColor}`, () => new CircleStyle({
      radius: chairRadius, fill: new Fill({ color: chairFillColor }),
    }));
    // Chairs need their own geometry, but Style objects are cheap: the
    // expensive part (the image) is shared.
    chairOffsets(place).forEach((offset) => {
      const [dx, dy] = rotatePoint(offset, place.rotation);
      styles.push(new Style({
        geometry: new Point([coordinate[0] + dx, coordinate[1] + dy]),
        image: chairImage,
        zIndex: 1,
      }));
    });
  }

  const shapeKey = [place.type, width, height, diameter, place.rotation, pxPerM.toFixed(3), fillColor, strokeColor, lineWidth].join('|');
  if (place.type === 'seat') {
    styles.push(...seatSilhouette(fillColor, strokeColor, lineWidth, Math.max(6, Math.max(width, height) * pxPerM), place.rotation));
  } else {
    styles.push(cached(styleCache, `shape|${shapeKey}`, () => {
      let shape;
      if (place.type === 'round_table') {
        shape = new CircleStyle({
          radius: Math.max(3, (diameter / 2) * pxPerM),
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: lineWidth }),
        });
      } else {
        const w = Math.max(4, width * pxPerM);
        const h = Math.max(4, height * pxPerM);
        shape = new RegularShape({
          points: 4,
          radius: Math.SQRT2 * (w / 2),
          angle: Math.PI / 4,
          scale: [1, h / w],
          rotation: (place.rotation * Math.PI) / 180,
          rotateWithView: true,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: lineWidth }),
        });
      }
      return new Style({ image: shape, zIndex: 2 });
    }));
  }

  const showNumber = place.type === 'seat' ? pxPerM >= LOD.seatNumbers : pxPerM >= LOD.tableNumbers;
  const text = label ?? (place.seat_number > 0 ? String(place.seat_number) : '');
  const fontSize = Math.round(Math.min(16, Math.max(9, pxPerM * (place.type === 'seat' ? 0.3 : 0.35))));
  // Long numbers (old venues store things like 16011) only show when they fit.
  const fitsInPlace = text.length * fontSize * 0.6 <= Math.max(width, height, diameter) * pxPerM * 1.2;
  if (showNumber && text && fitsInPlace) {
    styles.push(cached(styleCache, `text|${text}|${fontSize}|${ink}`, () => new Style({
      text: new TextStyle({
        text,
        font: `600 ${fontSize}px Rubik, system-ui, sans-serif`,
        fill: new Fill({ color: ink }),
      }),
      zIndex: 3,
    })));
  }

  if (state === 'locked' && pxPerM >= LOD.chairs) {
    const offsetY = -Math.round(Math.max(8, visualRadiusPx));
    const lockSize = Math.round(Math.max(9, pxPerM * 0.3));
    styles.push(cached(styleCache, `lock|${lockSize}|${offsetY}`, () => new Style({
      text: new TextStyle({ text: '🔒', font: `${lockSize}px sans-serif`, offsetY }),
      zIndex: 4,
    })));
  }

  return styles;
};

/** Stage, walkway, booths, zones… drawn under the places. */
export const shapeStyle = (shape: VenueShapeDTO, pxPerM: number, theme: ThemeMode, zoneColor?: string): Style[] => {
  const palette = mapPalette(theme);
  if (shape.type === 'zone') {
    const color = zoneColor ?? palette.muted;
    return [new Style({
      fill: new Fill({ color: withAlpha(color, 0.18) }),
      stroke: new Stroke({ color, width: 1.5, lineDash: [6, 4] }),
      text: shape.label ? new TextStyle({
        text: shape.label,
        font: `600 ${Math.round(Math.min(16, Math.max(10, pxPerM * 0.6)))}px Rubik, system-ui, sans-serif`,
        fill: new Fill({ color: palette.ink }),
        stroke: new Stroke({ color: palette.halo, width: 3 }),
        overflow: true,
      }) : undefined,
      zIndex: -1,
    })];
  }
  const fills: Partial<Record<VenueShapeDTO['type'], string>> = {
    stage: palette.stage,
    walkway: palette.walkway,
  };
  const fill = fills[shape.type] ?? palette.fixture;
  if (shape.type === 'text') {
    return [new Style({
      text: new TextStyle({
        text: shape.label ?? '',
        font: `600 ${Math.round(Math.max(10, (shape.height ?? 1) * pxPerM))}px Rubik, system-ui, sans-serif`,
        fill: new Fill({ color: palette.ink }),
        rotation: (shape.rotation * Math.PI) / 180,
      }),
    })];
  }
  return [new Style({
    fill: new Fill({ color: fill }),
    text: shape.label ? new TextStyle({
      text: shape.label.toUpperCase(),
      font: `700 ${Math.round(Math.min(14, Math.max(9, pxPerM * 0.8)))}px Rubik, system-ui, sans-serif`,
      fill: new Fill({ color: neutral[0] }),
      rotation: (shape.height ?? 0) > (shape.width ?? 0) * 2 ? -Math.PI / 2 : 0,
      overflow: true,
    }) : undefined,
    zIndex: -1,
  })];
};

/** Geometry for a shape: the zone polygon, or the rotated rectangle of anything else. */
export const shapeGeometry = (shape: Pick<VenueShapeDTO, 'type' | 'points' | 'x' | 'y' | 'width' | 'height' | 'rotation'>) => {
  if (shape.type === 'zone' && shape.points && shape.points.length >= 3) {
    const ring = shape.points.map((p) => [p.x, p.y]);
    ring.push([...ring[0]]);
    return new Polygon([ring]);
  }
  if (shape.type === 'text') {
    return new Point([shape.x ?? 0, shape.y ?? 0]);
  }
  const cx = shape.x ?? 0;
  const cy = shape.y ?? 0;
  const w = (shape.width ?? 1) / 2;
  const h = (shape.height ?? 1) / 2;
  const corners: [number, number][] = [[-w, -h], [w, -h], [w, h], [-w, h]];
  const ring = corners.map((c) => {
    const [dx, dy] = rotatePoint(c, shape.rotation ?? 0);
    return [cx + dx, cy + dy];
  });
  ring.push([...ring[0]]);
  return new Polygon([ring]);
};

export const shapeFeature = (shape: VenueShapeDTO) => {
  const feature = new Feature({ geometry: shapeGeometry(shape) });
  feature.set('shape', shape);
  feature.set('feature_type', 'shape');
  return feature;
};

export const isPlaceFeature = (feature: FeatureLike) => feature.get('feature_type') === 'place';
